import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { APIProvider, Map, Marker, useMap, useApiIsLoaded } from "@vis.gl/react-google-maps";
import { Button } from "./Button";
import { LoadingSpinner } from "./LoadingSpinner";
import {
  geocodeAddressForEnpalForYou,
  getGoogleMapsApiKey,
  hasRoofAddressDtoValues,
  roofAddressDtoFromGeocodeResult,
  saveRoofCoordinates,
  type GeocodeResultItem,
  type RoofCoordinatesAddressDto,
} from "../services/enpalForYouService";
import { Lead } from "../types";

const ITALY_DEFAULT = { lat: 42.5, lng: 12.5 };
const DEFAULT_ZOOM = 6;
const ROOF_ZOOM = 20;

type LatLng = google.maps.LatLngLiteral;

function isItalyBounds(lat: number, lng: number): boolean {
  return lat >= 35.4 && lat <= 47.2 && lng >= 6.2 && lng <= 19.5;
}

function sameLatLng(a: LatLng, b: LatLng): boolean {
  return Math.abs(a.lat - b.lat) < 1e-7 && Math.abs(a.lng - b.lng) < 1e-7;
}

/** API JSON often sends coordinates as strings; normalize before map bounds checks. */
function parseFiniteCoord(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string") {
    const n = Number(value.trim());
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function leadToHint(lead: Lead | null): string {
  if (!lead) {
    return "";
  }
  const parts = [lead.streetName, lead.streetNumber, lead.zip, lead.city, lead.province, "Italia"].filter(
    (p) => p != null && String(p).trim() !== ""
  ) as string[];
  return parts.join(", ");
}

/** True when the CRM line is only CAP / Italia (no street or city text yet). */
function leadStructuredAddressIsSparse(hint: string): boolean {
  const h = hint.trim();
  if (h === "") {
    return true;
  }
  return h.split(",").every((part) => {
    const p = part.trim();
    return p === "" || /^Italia$/i.test(p) || /^\d{5}$/.test(p);
  });
}

/** Line shown in “Indirizzo associato”: prefer full geocode when the lead only has sparse fields. */
function associatedAddressBoxLine(lead: Lead | null, geocodeFormatted: string | null): string {
  if (!lead) {
    return "";
  }
  const hint = leadToHint(lead).trim();
  const geo = (geocodeFormatted ?? "").trim();
  if (geo !== "" && leadStructuredAddressIsSparse(hint)) {
    return geo;
  }
  return hint;
}

/**
 * Same as Sales OS building: custCoordX = longitude, custCoordY = latitude.
 * Legacy lead coordX=lng, coordY=lat.
 */
function tryLeadInitialMarker(lead: Lead | null): LatLng | null {
  if (!lead) {
    return null;
  }
  const custLng = parseFiniteCoord(lead.custCoordX);
  const custLat = parseFiniteCoord(lead.custCoordY);
  if (custLat !== null && custLng !== null && isItalyBounds(custLat, custLng)) {
    return { lat: custLat, lng: custLng };
  }
  const legLng = parseFiniteCoord(lead.coordX);
  const legLat = parseFiniteCoord(lead.coordY);
  if (legLat !== null && legLng !== null && isItalyBounds(legLat, legLng)) {
    return { lat: legLat, lng: legLng };
  }
  return null;
}

type FlyTo = { position: LatLng; zoom: number; token: number };

function MapFlyToController({ flyTo }: { flyTo: FlyTo | null }): null {
  const map = useMap();
  useEffect(() => {
    if (!map || !flyTo) {
      return;
    }
    map.panTo(flyTo.position);
    map.setZoom(flyTo.zoom);
  }, [map, flyTo]);
  return null;
}

type MapBodyProps = {
  initialMarker: LatLng | null;
  onMarkerChange: (p: LatLng) => void;
  flyTo: FlyTo | null;
};

const MapBody: React.FC<MapBodyProps> = ({ initialMarker, onMarkerChange, flyTo }) => {
  const [marker, setMarker] = useState<LatLng | null>(initialMarker);
  const isLoaded = useApiIsLoaded();

  useEffect(() => {
    setMarker(initialMarker);
  }, [initialMarker]);

  const onMapClick = useCallback(
    (e: { detail: { latLng: google.maps.LatLngLiteral | null; placeId: string | null } }) => {
      const ll = e.detail.latLng;
      if (ll) {
        setMarker(ll);
        onMarkerChange(ll);
      }
    },
    [onMarkerChange]
  );

  const onMarkerDragEnd = useCallback(
    (e: google.maps.MapMouseEvent) => {
      const ll = e.latLng;
      if (ll) {
        const pos: LatLng = { lat: ll.lat(), lng: ll.lng() };
        setMarker(pos);
        onMarkerChange(pos);
      }
    },
    [onMarkerChange]
  );

  if (!isLoaded) {
    return (
      <div className="flex h-72 min-h-[280px] items-center justify-center rounded-2xl border border-enpal-downriver/10 bg-slate-50 sm:h-80">
        <LoadingSpinner />
      </div>
    );
  }

  const mapDefaultCenter = initialMarker ?? ITALY_DEFAULT;
  const mapDefaultZoom = initialMarker ? ROOF_ZOOM : DEFAULT_ZOOM;

  return (
    <Map
      className="h-72 w-full min-h-[280px] overflow-hidden rounded-2xl sm:h-80"
      defaultCenter={mapDefaultCenter}
      defaultZoom={mapDefaultZoom}
      mapTypeId="hybrid"
      gestureHandling="cooperative"
      disableDefaultUI={false}
      onClick={onMapClick}
    >
      <MapFlyToController flyTo={flyTo} />
      {marker && (
        <Marker
          position={marker}
          title="Punto sul tetto"
          draggable
          onDragEnd={onMarkerDragEnd}
        />
      )}
    </Map>
  );
};

type RoofLocationMapProps = {
  lead: Lead | null;
  leadLoading: boolean;
};

type RoofLocationMapInnerProps = RoofLocationMapProps & {
  onSaved?: () => void | Promise<void>;
};

const RoofLocationMap: React.FC<RoofLocationMapInnerProps> = ({ lead, leadLoading, onSaved }) => {
  /** Last lead-derived pin; used to clear stale map state only when coords disappear from the lead. */
  const prevLeadMarkerRef = useRef<LatLng | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [geocodeError, setGeocodeError] = useState<string | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [geoResults, setGeoResults] = useState<GeocodeResultItem[]>([]);
  const [marker, setMarker] = useState<LatLng | null>(() =>
    leadLoading ? null : tryLeadInitialMarker(lead)
  );
  const [lastSaved, setLastSaved] = useState<LatLng | null>(() =>
    leadLoading ? null : tryLeadInitialMarker(lead)
  );
  const [flyTo, setFlyTo] = useState<FlyTo | null>(() => {
    if (leadLoading) {
      return null;
    }
    const p = tryLeadInitialMarker(lead);
    return p ? { position: p, zoom: ROOF_ZOOM, token: Date.now() } : null;
  });
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  /** Structured address from last geocode pick; cleared when the pin is moved on the map (not sent if pin drifted). */
  const [roofAddressForSave, setRoofAddressForSave] = useState<RoofCoordinatesAddressDto | null>(null);
  /** Display-only for “Indirizzo associato”; kept when dragging the pin so the box does not jump back to CAP-only. */
  const [associatedBoxGeocodeLine, setAssociatedBoxGeocodeLine] = useState<string | null>(null);

  const onMarkerFromMap = useCallback((position: LatLng) => {
    setMarker(position);
    setRoofAddressForSave(null);
    setSaveMessage(null);
  }, []);

  const applyPosition = useCallback((position: LatLng) => {
    setMarker(position);
    setSaveMessage(null);
  }, []);

  const leadAddressFingerprint = useMemo(
    () =>
      lead == null
        ? ""
        : [
            lead.id,
            lead.streetName ?? "",
            lead.streetNumber ?? "",
            lead.zip ?? "",
            lead.city ?? "",
            lead.province ?? "",
          ].join("\u0001"),
    [lead?.id, lead?.streetName, lead?.streetNumber, lead?.zip, lead?.city, lead?.province]
  );

  /** Keep “Cerca un indirizzo” and the lead in sync when GET /lead returns updated street/city (e.g. after save). */
  useEffect(() => {
    if (leadLoading) {
      return;
    }
    if (lead == null) {
      setSearchQuery("");
      setAssociatedBoxGeocodeLine(null);
      setRoofAddressForSave(null);
      return;
    }
    const hint = leadToHint(lead);
    if (hint.trim() !== "") {
      setSearchQuery(hint);
    }
  }, [lead, leadLoading, leadAddressFingerprint]);

  /** Drop geocode-only box line once the lead has a full structured address (e.g. after refetch). */
  useEffect(() => {
    if (leadLoading || lead == null) {
      return;
    }
    if (!leadStructuredAddressIsSparse(leadToHint(lead))) {
      setAssociatedBoxGeocodeLine(null);
      setRoofAddressForSave(null);
    }
  }, [lead, leadLoading, leadAddressFingerprint]);

  useEffect(() => {
    if (leadLoading) {
      return;
    }
    const fromLead = tryLeadInitialMarker(lead);
    if (fromLead) {
      prevLeadMarkerRef.current = fromLead;
      setMarker(fromLead);
      setLastSaved(fromLead);
      if (!leadStructuredAddressIsSparse(leadToHint(lead))) {
        setRoofAddressForSave(null);
        setAssociatedBoxGeocodeLine(null);
      }
      setFlyTo({ position: fromLead, zoom: ROOF_ZOOM, token: Date.now() });
    } else {
      setLastSaved(null);
      const hadLeadPin = prevLeadMarkerRef.current !== null;
      prevLeadMarkerRef.current = null;
      if (hadLeadPin) {
        setMarker(null);
        setFlyTo(null);
        setRoofAddressForSave(null);
        setAssociatedBoxGeocodeLine(null);
        setSaveMessage(null);
        setSaveError(null);
        setGeoResults([]);
        setGeocodeError(null);
      }
    }
  }, [lead, leadLoading, lead?.id, lead?.custCoordX, lead?.custCoordY, lead?.coordX, lead?.coordY]);

  const runGeocode = useCallback(async (address: string) => {
    const trimmed = address.trim();
    if (trimmed === "") {
      setGeocodeError("Inserisci un indirizzo o una via.");
      setGeoResults([]);
      return;
    }
    setGeocodeError(null);
    setSearchLoading(true);
    setGeoResults([]);

    const results = await geocodeAddressForEnpalForYou(trimmed);
    setSearchLoading(false);
    if (results === null) {
      setGeocodeError("Impossibile avviare la ricerca. Controlla la connessione e riprova.");
      return;
    }
    if (results.length === 0) {
      setGeocodeError("Nessun indirizzo trovato. Prova con strada, numero, CAP, città.");
      return;
    }
    if (results.length === 1) {
      const r = results[0];
      const position = { lat: r.lat, lng: r.lng };
      applyPosition(position);
      const addrDto = roofAddressDtoFromGeocodeResult(r);
      setRoofAddressForSave(hasRoofAddressDtoValues(addrDto) ? addrDto : null);
      setAssociatedBoxGeocodeLine(r.formattedAddress.trim() !== "" ? r.formattedAddress : null);
      setFlyTo({ position, zoom: ROOF_ZOOM, token: Date.now() });
      setGeocodeError(null);
    } else {
      setGeoResults(results);
      setGeocodeError("Seleziona un risultato sotto o affina l’indirizzo.");
    }
  }, [applyPosition]);

  const pickResult = (r: GeocodeResultItem) => {
    const position = { lat: r.lat, lng: r.lng };
    applyPosition(position);
    const addrDto = roofAddressDtoFromGeocodeResult(r);
    setRoofAddressForSave(hasRoofAddressDtoValues(addrDto) ? addrDto : null);
    setAssociatedBoxGeocodeLine(r.formattedAddress.trim() !== "" ? r.formattedAddress : null);
    setFlyTo({ position, zoom: ROOF_ZOOM, token: Date.now() });
    setGeoResults([]);
    setGeocodeError(null);
  };

  const onSave = async () => {
    if (!marker) {
      return;
    }
    if (lastSaved && sameLatLng(marker, lastSaved)) {
      return;
    }
    setSaveLoading(true);
    setSaveError(null);
    setSaveMessage(null);
    const { ok, error } = await saveRoofCoordinates(
      marker.lat,
      marker.lng,
      roofAddressForSave && hasRoofAddressDtoValues(roofAddressForSave) ? roofAddressForSave : undefined
    );
    setSaveLoading(false);
    if (ok) {
      setLastSaved({ ...marker });
      setSaveMessage("Posizione salvata.");
      void onSaved?.();
    } else {
      setSaveError(error ?? "Salvataggio non riuscito.");
    }
  };

  const isDirty = Boolean(marker) && (lastSaved === null || (marker != null && lastSaved != null && !sameLatLng(marker, lastSaved)));
  const canSave = isDirty && !saveLoading;
  const associatedBoxLine = associatedAddressBoxLine(lead, associatedBoxGeocodeLine);

  return (
    <>
      <p className="text-base leading-7 text-enpal-stone-grey">
        Cerca l&apos;indirizzo, poi <strong className="font-semibold text-enpal-downriver">trascina il segnalino</strong>{" "}
        sul tuo tetto. La vista ibrida combina mappa e satellite.
      </p>

      <div className="mt-5">
        <label className="form-label" htmlFor="roof-search">
          Cerca un indirizzo
        </label>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
          <input
            id="roof-search"
            className="form-input flex-1"
            type="search"
            autoComplete="street-address"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setGeocodeError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                void runGeocode(searchQuery);
              }
            }}
            placeholder="es. Via Roma 10, 00100 Roma"
          />
          <Button
            type="button"
            onClick={() => {
              void runGeocode(searchQuery);
            }}
            className="min-h-[48px] shrink-0 justify-center sm:min-w-[120px]"
            disabled={searchLoading}
          >
            {searchLoading ? "Cerca…" : "Cerca"}
          </Button>
        </div>
      </div>

      {geocodeError && <p className="error-banner mt-3">{geocodeError}</p>}

      {geoResults.length > 1 && (
        <ul className="enpal-subtle-card mt-4 list-none space-y-2 p-0">
          {geoResults.slice(0, 8).map((r) => (
            <li key={r.placeId}>
              <button
                type="button"
                className="w-full rounded-xl border border-slate-200/80 bg-white py-2.5 pl-3 pr-3 text-left text-sm text-enpal-downriver transition hover:border-enpal-dodger-blue/30 hover:bg-slate-50"
                onClick={() => {
                  pickResult(r);
                }}
              >
                {r.formattedAddress}
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-4 overflow-hidden rounded-2xl border border-enpal-downriver/10">
        <MapBody initialMarker={marker} onMarkerChange={onMarkerFromMap} flyTo={flyTo} />
      </div>

      {marker && (
        <p className="mt-3 text-sm text-enpal-stone-grey">
          Punto: {marker.lat.toFixed(5)}, {marker.lng.toFixed(5)} — trascina il segnalino o tocca la mappa per
          spostarlo.
        </p>
      )}

      {lead && associatedBoxLine.trim() !== "" && (
        <div className="enpal-subtle-card mt-4 p-3">
          <p className="text-sm font-medium text-enpal-downriver">Indirizzo associato</p>
          <p className="mt-1 text-sm text-enpal-stone-grey">{associatedBoxLine}</p>
        </div>
      )}

      {!marker && <p className="mt-3 text-sm text-enpal-stone-grey">Cerca un indirizzo o tocca la mappa per iniziare.</p>}

      <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <Button
          type="button"
          onClick={() => {
            void onSave();
          }}
          className="min-w-[200px] justify-center"
          disabled={!canSave}
        >
          {saveLoading ? "Salvataggio…" : "Salva posizione"}
        </Button>
        {saveMessage && <p className="text-sm font-medium text-enpal-grass-green">{saveMessage}</p>}
        {saveError && <p className="error-banner m-0 flex-1 py-2 text-sm sm:self-center">{saveError}</p>}
      </div>
    </>
  );
};

export type RoofLocationSectionProps = {
  lead: Lead | null;
  leadLoading: boolean;
  /** e.g. refetch lead so address/coords from GET /enpal-for-you/lead update after save */
  onRoofLocationSaved?: () => void | Promise<void>;
};

const CONFIG_MSG =
  "Mappa disabilitata: l'app non riesce a scaricare la chiave di configurazione. Verifica REACT_APP_API_URL, l'accesso al portale, e che il server esponga GET /enpal-for-you/maps-api-key.";

export const RoofLocationSection: React.FC<RoofLocationSectionProps> = (props) => {
  const [apiKey, setApiKey] = useState<string | null | undefined>(undefined);
  const [keyError, setKeyError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setKeyError(null);
      const key = await getGoogleMapsApiKey();
      if (cancelled) {
        return;
      }
      if (key) {
        setApiKey(key);
        return;
      }
      setApiKey(null);
      setKeyError("Chiave mappa non disponibile. Riprova più tardi o controlla l'accesso al servizio.");
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section className="dashboard-main-card" data-testid="roof-location-section">
      <div className="mb-5 flex items-center justify-between gap-4 border-b border-slate-200 pb-4">
        <div>
          <p className="dashboard-kicker">Dove sorge il tuo impianto</p>
          <h2 className="mt-2 text-2xl font-bold text-enpal-downriver sm:text-3xl">La tua casa sulla mappa</h2>
        </div>
        <span className="dashboard-chip">Mappa</span>
      </div>

      {apiKey === undefined && (
        <div className="flex min-h-[200px] flex-col items-center justify-center gap-3 py-6">
          <LoadingSpinner />
          <p className="text-sm text-enpal-stone-grey">Caricamento mappa…</p>
        </div>
      )}

      {apiKey && (
        <APIProvider apiKey={apiKey} language="it" region="IT" solutionChannel="">
          <RoofLocationMap
            lead={props.lead}
            leadLoading={props.leadLoading}
            onSaved={props.onRoofLocationSaved}
          />
        </APIProvider>
      )}

      {apiKey === null && keyError && (
        <p className="error-banner" role="status">
          {keyError}
        </p>
      )}

      {apiKey === null && !keyError && (
        <p className="info-banner" role="status">
          {CONFIG_MSG}
        </p>
      )}
    </section>
  );
};
